import React, { useEffect } from 'react';
import { Database, Loader2, Check, AlertCircle } from 'lucide-react';
import { useSearchIndex, refreshSearchIndex, runSearchIndex } from '../services/searchIndexStore';

/**
 * Notebook context-menu row: how much of this notebook the Study Companion can search, and a
 * click to index what is missing. The menu stays open while it runs so the progress is visible;
 * indexing continues in the background if the menu is closed.
 */
export default function NotebookIndexMenuItem({ notebookId, vn }: { notebookId: string; vn: boolean }) {
    const index = useSearchIndex();

    // Coverage is scanned when a menu first asks for it, then kept for the session.
    useEffect(() => { if (!index.coverage && !index.scanning && !index.error) void refreshSearchIndex(); }, []);

    const c = index.coverage?.[notebookId];
    const runningHere = !!index.running && (index.running.notebookId === null || index.running.notebookId === notebookId);
    const busyElsewhere = !!index.running && !runningHere;
    const pct = c && c.total > 0 ? Math.round((c.indexed / c.total) * 100) : 0;
    const complete = !!c && c.indexed >= c.total;
    const missing = c ? c.total - c.indexed : 0;

    let detail: string;
    if (index.error && !c) detail = vn ? 'Không kiểm tra được — nhấn để thử lại' : 'Could not check — click to retry';
    else if (!index.coverage) detail = vn ? 'Đang kiểm tra…' : 'Checking…';
    else if (!c || c.total === 0) detail = vn ? 'Notebook chưa có ghi chú nào để lập chỉ mục' : 'No notes to index yet';
    else if (runningHere) detail = vn ? `Đang lập chỉ mục… ${c.indexed}/${c.total} ghi chú` : `Indexing… ${c.indexed}/${c.total} notes`;
    else if (complete) detail = vn ? `Đủ ${c.total} ghi chú — AI tìm được toàn bộ` : `All ${c.total} notes searchable by AI`;
    else if (busyElsewhere) detail = vn ? `${c.indexed}/${c.total} ghi chú · đang bận notebook khác` : `${c.indexed}/${c.total} notes · busy with another notebook`;
    else detail = vn ? `${c.indexed}/${c.total} ghi chú · nhấn để lập chỉ mục ${missing} ghi chú còn lại` : `${c.indexed}/${c.total} notes · click to index the remaining ${missing}`;

    const canRun = !index.running && !index.scanning && ((index.error && !c) || (!!c && !complete && c.total > 0));
    const onClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // keep the menu open so progress stays visible
        if (!canRun) return;
        if (index.error && !c) void refreshSearchIndex();
        else void runSearchIndex(notebookId).catch(() => {});
    };

    return (
        <button
            onClick={onClick}
            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors ${canRun ? 'hover:bg-indigo-50 dark:hover:bg-indigo-900/20' : 'cursor-default'}`}
        >
            {runningHere || !index.coverage && !index.error
                ? <Loader2 size={14} className="text-indigo-500 shrink-0 animate-spin" />
                : index.error && !c ? <AlertCircle size={14} className="text-amber-500 shrink-0" />
                : complete ? <Check size={14} className="text-emerald-600 shrink-0" />
                : <Database size={14} className="text-indigo-500 shrink-0" />}
            <span className="flex flex-col min-w-0 flex-1">
                <span className="text-gray-700 dark:text-gray-200 flex items-center justify-between gap-2">
                    <span>{vn ? 'Chỉ mục AI' : 'AI search index'}</span>
                    {c && c.total > 0 && <span className={`text-[11px] font-semibold tabular-nums ${complete ? 'text-emerald-600' : 'text-indigo-500'}`}>{pct}%</span>}
                </span>
                <span className="text-[11px] text-gray-400 dark:text-slate-500">{detail}</span>
                {c && c.total > 0 && !complete && (
                    <span className="mt-1 h-1 rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden">
                        <span className="block h-full rounded-full bg-indigo-500 transition-all duration-300" style={{ width: `${pct}%` }} />
                    </span>
                )}
            </span>
        </button>
    );
}
